(() => {
  const navigation = window.AGWSupportPageNavigation;

  if (navigation) {
    navigation.init(document);
    return;
  }

  const jumpTargetId = 'support-page-navigation';
  const returnsSectionIds = new Set([
    'returns',
    'not-accepted',
    'refunds',
    'exchanges',
    'warranties',
    'sale-items',
    'shipping-notes',
  ]);

  const promoteToH2 = (heading) => {
    const replacement = document.createElement('h2');

    Array.from(heading.attributes).forEach((attribute) => {
      replacement.setAttribute(attribute.name, attribute.value);
    });

    while (heading.firstChild) {
      replacement.append(heading.firstChild);
    }

    heading.replaceWith(replacement);
  };

  const normalizeReturnsHeadings = (content) => {
    if (!content.closest('.page--support-returns-exchanges-warranties')) {
      return;
    }

    Array.from(content.children).forEach((element) => {
      if (!element.matches('h3')) {
        return;
      }

      const headingText = element.textContent.trim().toLowerCase();
      const isJumpHeading = headingText === 'jump to:' || headingText === 'jump to';

      if (isJumpHeading || returnsSectionIds.has(element.id)) {
        promoteToH2(element);
      }
    });
  };

  const removeReturnsLegacyCta = (content) => {
    if (!content.closest('.page--support-returns-exchanges-warranties')) {
      return;
    }

    const normalizeText = (value) => value.replace(/\s+/g, ' ').trim();
    const expectedHeading = 'Need help with a return, exchange, or warranty claim?';
    const expectedEmail = 'CustomerSupport@AmericanGasWorks.com';
    const paragraphs = Array.from(content.children).filter((element) => element.matches('p'));

    const actionParagraph = paragraphs.find((paragraph) => {
      const paragraphText = normalizeText(paragraph.textContent);
      const emailLink = Array.from(paragraph.querySelectorAll('a[href^="mailto:"]')).find(
        (link) =>
          normalizeText(link.textContent) === expectedEmail &&
          link.getAttribute('href').toLowerCase() === `mailto:${expectedEmail}`.toLowerCase()
      );
      const precedingParagraph = paragraph.previousElementSibling;
      const hasExpectedHeading =
        paragraphText.includes(expectedHeading) ||
        (precedingParagraph &&
          precedingParagraph.matches('p') &&
          normalizeText(precedingParagraph.textContent) === expectedHeading);

      return paragraphText.includes(expectedEmail) && emailLink && hasExpectedHeading;
    });

    if (!actionParagraph) {
      return;
    }

    const actionText = normalizeText(actionParagraph.textContent);
    const headingIsInline = actionText.includes(expectedHeading);
    const precedingParagraph = actionParagraph.previousElementSibling;
    const headingIsSeparate =
      precedingParagraph &&
      precedingParagraph.matches('p') &&
      normalizeText(precedingParagraph.textContent) === expectedHeading;

    if (!headingIsInline && !headingIsSeparate) {
      return;
    }

    actionParagraph.remove();

    if (headingIsSeparate) {
      precedingParagraph.remove();
    }
  };

  const removeShippingLegacyCta = (content) => {
    if (!content.closest('.page--support-shipping-delivery')) {
      return;
    }

    const normalizeText = (value) => value.replace(/\s+/g, ' ').trim();
    const expectedHeading = 'Still have questions?';
    const expectedAction = 'Reach out to our support team';
    const expectedLegacySentence = `${expectedAction}—we’re happy to help!`;
    const paragraphs = Array.from(content.querySelectorAll('p'));

    const actionParagraph = paragraphs.find((paragraph) => {
      const paragraphText = normalizeText(paragraph.textContent);
      const supportLink = Array.from(paragraph.querySelectorAll('a')).find(
        (link) =>
          normalizeText(link.textContent) === expectedAction &&
          link.getAttribute('href') === '/pages/contact-us'
      );
      const precedingParagraph = paragraph.previousElementSibling;
      const headingIsSeparate =
        precedingParagraph &&
        precedingParagraph.matches('p') &&
        normalizeText(precedingParagraph.textContent) === expectedHeading;
      const exactInlineTexts = [
        `${expectedHeading}${expectedLegacySentence}`,
        `${expectedHeading} ${expectedLegacySentence}`,
      ];
      const hasExactInlineCta = exactInlineTexts.includes(paragraphText);
      const hasExactSeparateCta = headingIsSeparate && paragraphText === expectedLegacySentence;

      return supportLink && (hasExactInlineCta || hasExactSeparateCta);
    });

    if (!actionParagraph) {
      return;
    }

    const actionText = normalizeText(actionParagraph.textContent);
    const headingIsInline = actionText.includes(expectedHeading);
    const precedingParagraph = actionParagraph.previousElementSibling;
    const headingIsSeparate =
      precedingParagraph &&
      precedingParagraph.matches('p') &&
      normalizeText(precedingParagraph.textContent) === expectedHeading;

    actionParagraph.remove();

    if (!headingIsInline && headingIsSeparate) {
      precedingParagraph.remove();
    }
  };

  const createBackToTopLink = () => {
    const link = document.createElement('a');
    link.className = 'support-page__back-to-top';
    link.href = `#${jumpTargetId}`;
    link.textContent = '↑ Back to Top';
    link.setAttribute('aria-label', 'Back to Top: Jump To navigation');
    return link;
  };

  const initializeContent = (content) => {
    if (content.dataset.backToTopInitialized === 'true') {
      return;
    }

    removeShippingLegacyCta(content);
    removeReturnsLegacyCta(content);
    normalizeReturnsHeadings(content);

    const directChildren = Array.from(content.children);
    const headings = directChildren.filter((element) => element.matches('h2'));
    const jumpTarget = headings[0];
    const majorHeadings = headings.slice(1);

    if (!jumpTarget || majorHeadings.length === 0) {
      return;
    }

    jumpTarget.id = jumpTargetId;
    jumpTarget.tabIndex = -1;
    jumpTarget.classList.add('support-page__jump-target');

    majorHeadings.forEach((heading) => {
      let boundary = heading.nextElementSibling;

      while (boundary && !boundary.matches('hr, h2')) {
        boundary = boundary.nextElementSibling;
      }

      const link = createBackToTopLink();

      if (boundary) {
        boundary.before(link);
      } else {
        content.append(link);
      }
    });

    content.addEventListener('click', (event) => {
      const link = event.target.closest('.support-page__back-to-top');

      if (!link || !content.contains(link)) {
        return;
      }

      event.preventDefault();

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      try {
        jumpTarget.focus({ preventScroll: true });
      } catch (error) {
        jumpTarget.focus();
      }

      jumpTarget.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    });

    content.dataset.backToTopInitialized = 'true';
  };

  const init = (root = document) => {
    root.querySelectorAll('.page--support .support-page__rte').forEach(initializeContent);
  };

  window.AGWSupportPageNavigation = { init };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
